<template>
  <PaymentRequestsControlBar
    :filters-disabled="filterDisabled"
    :clients="clientOptions"
    :job-titles="jobTitleOptions"
    @filters-updated="filterUpdated"
  />
  <BaseDataTable
    v-if="filterReady"
    :entries="tableData.rows"
    :columns="tableData.columns"
    :loading="$apollo.loading"
    :paginator-info="paginator"
    search-disabled
    class="f-12"
    data-test="payment-requests-list-data-table"
    @search="search"
  >
    <template #Date="{ row }">
      {{ row.issuedAt ? formatDate(row.issuedAt) : formatDate(row.createdAt) }}
    </template>

    <template #Ref="{ row }">#{{ row.billNumber }}</template>

    <template #Status="{ row }">
      <WdsTag
        sm
        :warning="
          row.workerStatus === PaymentRequestWorkerStatus.Processing ||
          row.workerStatus === PaymentRequestWorkerStatus.Draft
        "
        :error="
          row.workerStatus === PaymentRequestWorkerStatus.Cancelled ||
          row.workerStatus === PaymentRequestWorkerStatus.Rejected
        "
        :primary="row.workerStatus === PaymentRequestWorkerStatus.Requested"
        :success="row.workerStatus === PaymentRequestWorkerStatus.Approved"
        :gray="row.workerStatus === PaymentRequestWorkerStatus.Completed"
      >
        <div class="tw-flex">
          <div
            v-if="row.workerStatus === PaymentRequestWorkerStatus.Draft"
            class="tw-m-1 tw-h-2 tw-w-2 tw-bg-orange-500 tw-rounded-full"
          />
          <popover-payment-request-status
            v-cloak
            v-if="row.workerStatus !== PaymentRequestWorkerStatus.Draft"
            :timeline="row.timeline"
          >
            {{ getWorkerStatusLabel(row.workerStatus) }}
          </popover-payment-request-status>
          <span v-if="row.workerStatus === PaymentRequestWorkerStatus.Draft">
            {{ getWorkerStatusLabel(row.workerStatus) }}
          </span>
        </div>
      </WdsTag>
    </template>

    <template #Client="{ row }">
      {{ row.company.name }}
    </template>

    <template v-if="viewerIsStaffingAgency" #Worker="{ row }">
      {{ row.freelancer?.name || '' }}
    </template>

    <template #JobTitle="{ row }">
      <a :href="`/hires/${row.hire.localId}/overview`" :title="row.job.name">
        {{ row.job.name.length > 30 ? row.job.name.slice(0, 50) + '...' : row.job.name }}
      </a>
    </template>

    <template #Invoice="{ row }">
      <span v-for="invoice in invoiceReferences(row)" :key="`invoice-${invoice.number}`">
        <a
          :href="invoice.pdfUrl ?? `/bills/${row.localId}/invoice-pdf`"
          :data-test="`payment-request-invoice-link-${invoice.number}`"
          target="_blank"
        >
          <WdsLabel xs primary subtle>
            {{ invoice.number }}
          </WdsLabel>
        </a>
      </span>
      <span v-if="invoiceReferences(row).length === 0">-</span>
    </template>

    <template #Amount="{ row }">
      <span v-if="isRecruiter && !row.recruiterPayoutAmount">-</span>
      <span
        v-else
        :class="
          (row.workerStatus === PaymentRequestWorkerStatus.Rejected ||
            row.workerStatus === PaymentRequestWorkerStatus.Cancelled) &&
          'tw-line-through'
        "
      >
        {{ row.currencySymbol }}&nbsp;{{
          isRecruiter
            ? row.recruiterPayoutAmount.withoutVat.inDec
            : row.totalBilledMoneyWithExpenses.withoutVat.inDec
        }}
      </span>
    </template>

    <template #Timesheet="{ row }">
      <span v-if="row.hasWorksomeTimesheet" :title="$t('Worksome timesheet')">
        <WdsIcon name="document" :size="16"></WdsIcon>
      </span>
      <span v-else-if="row.hasTimesheet" :title="$t('Attached timesheet')">
        <WdsIcon name="document-import" :size="16"></WdsIcon>
      </span>
    </template>

    <template #Action="{ row }">
      <div class="tw-flex tw-justify-center">
        <PaymentRequestActionMenu
          v-if="
            row.viewerCanManage &&
            row.canBeEdited &&
            (row.workerStatus === PaymentRequestWorkerStatus.Requested ||
              row.workerStatus === PaymentRequestWorkerStatus.Draft)
          "
          :payment-request="row"
          @payment-request-deleted="$apollo.queries.data.refetch()"
        />
        <a v-else-if="row.viewerCanManage" :href="`/bills/${row.localId}/edit`">{{ $t('View') }}</a>
        <span v-else>-</span>
      </div>
    </template>

    <template #no-results>
      <div class="tw-justify-center" data-test="no-payment-requests-found">
        {{ $apollo.loading ? $t('Loading') : $t('There are no existing payment requests') }}
      </div>
    </template>
  </BaseDataTable>
</template>

<script>
import gql from 'graphql-tag'
import { inject } from 'vue'
import moment from 'moment'
import { WdsIcon, WdsLabel, WdsTag } from '@worksome/wds-components'
import BaseDataTable from '@/components/BaseDataTable/BaseDataTable.vue'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import PaymentRequestsControlBar from '@/components/RequestPaymentDashboard/PaymentRequestsControlBar.vue'
import HashMixin from '@/mixins/MethodHash'
import PopoverPaymentRequestStatus from '@/components/PopoverPaymentRequestStatus.vue'
import PaymentRequestActionMenu from '@/components/RequestPaymentDashboard/PaymentRequestActionMenu.vue'
import { PaymentRequestWorkerStatus } from '@/gql/graphql'
import { getWorkerStatusLabel } from '@/components/PaymentRequest/utilities/statusFormatting'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

export default {
  components: {
    PaymentRequestActionMenu,
    WdsIcon,
    PopoverPaymentRequestStatus,
    WdsTag,
    PaymentRequestsControlBar,
    WdsLabel,
    BaseDataTable,
  },
  mixins: [HashMixin],
  setup() {
    const viewerIsStaffingAgency = inject('viewerIsStaffingAgency')
    const { isRecruiter, actsAsSupplier } = useAccountCapabilities()
    return { viewerIsStaffingAgency, isRecruiter, actsAsSupplier }
  },
  apollo: {
    data: {
      skip() {
        return !this.filterReady
      },
      query: gql`
        fragment BillFields on Bill {
          # Loading the hire reloads it, so it comes before the fields that eager load relations onto it.
          hire {
            localId
          }
          globalId
          publicId
          localId
          createdAt
          issuedAt
          cancelledAt
          approvedAt
          autoApprovedAt
          rejectedAt
          payedAt
          payrolledAt
          dueAt
          processedAt
          completedAt
          billNumber
          currencySymbol
          workerStatus
          canBeEdited
          viewerCanManage
          totalBilledMoneyWithExpenses @skip(if: $viewerIsRecruiter) {
            withoutVat {
              inDec
            }
          }
          recruiterPayoutAmount @include(if: $viewerIsRecruiter) {
            withoutVat {
              inDec
            }
          }
          supplierInvoices {
            pdfUrl
            number
          }
          invoices {
            number
          }
          company {
            globalId
            localId
            name
          }
          job: yjob {
            globalId
            localId
            name
          }
          freelancer {
            name
          }
          hasWorksomeTimesheet
          hasTimesheet
          timeline {
            name
            description
            date
            completed
            failed
            overdue
          }
        }
        query Bills(
          $filter: BillsFilterInput
          $order: [BillsOrderByClause!]
          $page: Int!
          $count: Int!
          $viewerIsRecruiter: Boolean!
          $viewerActsAsSupplier: Boolean!
        ) {
          data: me {
            globalId
            localId
            activeAccount {
              globalId
              ... on Freelancer {
                obfuscatedBankAccountData
              }
              ... on Recruiter {
                obfuscatedBankAccountData
              }
              user {
                globalId
                localId
                name
              }
              __typename
              ... on HasBills {
                bills(
                  filter: $filter
                  orderBy: $order
                  first: $count
                  page: $page
                  scopes: ["draft"]
                ) @skip(if: $viewerActsAsSupplier) {
                  paginatorInfo {
                    count
                    currentPage
                    total
                    ...PaginatorInfoFragment
                  }
                  data {
                    ...BillFields
                  }
                }
              }
              ... on Company {
                supplierBills(
                  filter: $filter
                  orderBy: $order
                  first: $count
                  page: $page
                  scopes: ["draft"]
                ) {
                  paginatorInfo {
                    count
                    currentPage
                    total
                    ...PaginatorInfoFragment
                  }
                  data {
                    ...BillFields
                  }
                }
              }
            }
          }
        }
        ${BaseDataTable.fragments.pagination}
      `,
      debounce: 250,
      errorPolicy: 'all',
      variables() {
        return {
          filter: { ...this.filter },
          order: [
            {
              field: 'CREATED_AT',
              order: 'DESC',
            },
          ],
          page: this.paginationSettings.page,
          count: this.paginationSettings.displayPerPage,
          viewerIsRecruiter: this.isRecruiter,
          viewerActsAsSupplier: this.actsAsSupplier,
        }
      },
      result({ data }) {
        this.updateJobTitlesFromBills()
        this.updateClientsFromBills()
        const account = data?.data?.activeAccount
        if (!account) {
          return
        }
        this.$emit('account-info', account.obfuscatedBankAccountData ?? '')
      },
    },
  },
  data() {
    return {
      filter: {},
      action: 'default',
      paginationSettings: {
        page: 1,
        displayPerPage: 50,
      },
      formatDate: (date) => {
        const momentDate = moment(date)
        return momentDate.format('YYYY-MM-DD')
      },
      loading: true,
      currentUrl: this.getCurrentUrl(),
      clientOptions: [],
      jobTitleOptions: [],
    }
  },
  computed: {
    PaymentRequestWorkerStatus() {
      return PaymentRequestWorkerStatus
    },
    moment: () => moment,
    filterDisabled() {
      return this.tableData.rows.length === 0
    },
    filterReady() {
      return Object.keys(this.filter).length > 0
    },
    isCompanySupplier() {
      return this.viewerIsStaffingAgency && this?.data?.activeAccount?.__typename === 'Company'
    },
    activeBillsData() {
      const account = this?.data?.activeAccount
      return this.isCompanySupplier ? account?.supplierBills : account?.bills
    },
    paginator() {
      return this.activeBillsData?.paginatorInfo
    },
    totalElements() {
      return this.paginator?.total ?? 0
    },
    rows() {
      return this.activeBillsData?.data || []
    },
    tableData() {
      return new TableDataHandler({
        rows: this.rows,
        columns: this.columns,
      })
    },
    columns() {
      const cols = [
        {
          title: 'Date',
          key: 'createdAt',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Ref.#',
          key: 'id',
          slot: 'Ref',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Status',
          key: 'status',
          slot: 'Status',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Client',
          key: 'client',
          slot: 'Client',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
      ]
      if (this.viewerIsStaffingAgency) {
        cols.push({
          title: 'Worker',
          key: 'worker',
          slot: 'Worker',
          orderAble: false,
          ignoreClickForRowSelection: true,
        })
      }
      cols.push(
        {
          title: 'Hire title',
          key: 'yjob.job',
          slot: 'JobTitle',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Invoice',
          key: 'invoice',
          slot: 'Invoice',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Amount',
          key: 'amount',
          slot: 'Amount',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: '',
          key: 'timesheet',
          slot: 'Timesheet',
          class: 'nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: '',
          key: 'action',
          slot: 'Action',
          class: 'text-right',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
      )
      return cols
    },
  },
  methods: {
    getWorkerStatusLabel,
    invoiceReferences(row) {
      return [...(row.supplierInvoices || []), ...(row.invoices || [])]
    },
    updateJobTitlesFromBills() {
      if (this.jobTitleOptions.length > 0) {
        return
      }
      const jobTitles = []
      this.rows.forEach((bill) => {
        if (bill.job && !jobTitles.find((job) => job.value === bill.job.localId)) {
          jobTitles.push({
            value: bill.job.localId,
            label: bill.job.name,
          })
        }
      })
      this.jobTitleOptions = jobTitles.sort((a, b) => (a.label || '').localeCompare(b.label || ''))
    },
    updateClientsFromBills() {
      if (this.clientOptions.length > 0) {
        return
      }
      const clients = []
      this.rows.forEach((bill) => {
        if (bill.company && !clients.find((client) => client.value === bill.company.localId)) {
          clients.push({
            value: bill.company.localId,
            label: bill.company.name,
          })
        }
      })
      this.clientOptions = clients.sort((a, b) => (a.label || '').localeCompare(b.label || ''))
    },
    search(options) {
      this.paginationSettings.displayPerPage = options.perPage
      this.paginationSettings.page = options.page + 1
    },
    getCurrentUrl() {
      return encodeURIComponent(window.location.pathname + window.location.search)
    },
    filterUpdated($event) {
      this.currentUrl = this.getCurrentUrl()
      // Since the filter might fire at random, we only update our copy when the new filter is different from ours.
      // We also reset out page to one, since the amount of data we have might change such that there are fewer pages
      // for us to paginate.
      if (this.hash(this.filter) !== this.hash($event)) {
        this.filter = $event
      }
    },
  },
}
</script>
